import {
    Assessment,
    AssessmentsDocument,
    Choice,
    ChoiceSet,
    EMPTY_ASSESSMENTS_DOCUMENT,
    Question,
    QuestionType
} from "./assessmentsTypes";
import {
    MAX_ASSESSMENTS,
    MAX_CHOICE_SETS,
    MAX_CHOICES_PER_SET,
    MAX_QUESTIONS_PER_ASSESSMENT,
    clampDescription,
    clampPrompt,
    clampTitle
} from "./assessmentsSchema";

function newId(prefix: string): string {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
        return `${prefix}-${crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function cloneDocument(document: AssessmentsDocument): AssessmentsDocument {
    return JSON.parse(JSON.stringify(document)) as AssessmentsDocument;
}

export function documentsEqual(left: AssessmentsDocument, right: AssessmentsDocument): boolean {
    return JSON.stringify(left) === JSON.stringify(right);
}

export function normalizeDocument(raw: unknown): AssessmentsDocument {
    if (!raw || typeof raw !== "object") {
        return cloneDocument(EMPTY_ASSESSMENTS_DOCUMENT);
    }
    const data = raw as Partial<AssessmentsDocument>;
    return {
        schema_version: typeof data.schema_version === "number" ? data.schema_version : 1,
        choice_sets: Array.isArray(data.choice_sets) ? data.choice_sets : [],
        assessments: Array.isArray(data.assessments) ? data.assessments : [],
        updated_at: typeof data.updated_at === "number" ? data.updated_at : undefined,
        updated_by: typeof data.updated_by === "string" ? data.updated_by : undefined
    };
}

function findAssessment(document: AssessmentsDocument, assessmentId: string): Assessment | undefined {
    return document.assessments.find(item => item.id === assessmentId);
}

function findChoiceSet(document: AssessmentsDocument, choiceSetId: string): ChoiceSet | undefined {
    return document.choice_sets.find(item => item.id === choiceSetId);
}

function isChoiceSetReferenced(document: AssessmentsDocument, choiceSetId: string): boolean {
    return document.assessments.some(assessment =>
        assessment.questions.some(
            question => question.type === "multiple_choice" && question.choice_set_id === choiceSetId
        )
    );
}

export function addAssessment(document: AssessmentsDocument): {
    document: AssessmentsDocument;
    assessment: Assessment;
} {
    const draft = cloneDocument(document);
    if (draft.assessments.length >= MAX_ASSESSMENTS) {
        throw new Error(`Maximum ${MAX_ASSESSMENTS} assessments allowed`);
    }
    const assessment: Assessment = {
        id: newId("as"),
        title: "",
        description: "",
        questions: []
    };
    draft.assessments.push(assessment);
    return { document: draft, assessment };
}

export function updateAssessment(
    document: AssessmentsDocument,
    assessmentId: string,
    fields: { title?: string; description?: string }
): AssessmentsDocument {
    const draft = cloneDocument(document);
    const assessment = findAssessment(draft, assessmentId);
    if (!assessment) {
        throw new Error(`Assessment not found: ${assessmentId}`);
    }
    if (fields.title !== undefined) {
        assessment.title = clampTitle(fields.title.trim());
    }
    if (fields.description !== undefined) {
        assessment.description = clampDescription(fields.description.trim());
    }
    return draft;
}

export function deleteAssessment(document: AssessmentsDocument, assessmentId: string): AssessmentsDocument {
    const draft = cloneDocument(document);
    draft.assessments = draft.assessments.filter(item => item.id !== assessmentId);
    return draft;
}

export function addQuestion(
    document: AssessmentsDocument,
    assessmentId: string,
    questionType: QuestionType,
    choiceSetId?: string
): { document: AssessmentsDocument; question: Question } {
    const draft = cloneDocument(document);
    const assessment = findAssessment(draft, assessmentId);
    if (!assessment) {
        throw new Error(`Assessment not found: ${assessmentId}`);
    }
    if (assessment.questions.length >= MAX_QUESTIONS_PER_ASSESSMENT) {
        throw new Error(`Maximum ${MAX_QUESTIONS_PER_ASSESSMENT} questions per assessment`);
    }

    let question: Question;
    if (questionType === "scale") {
        question = {
            id: newId("q"),
            type: "scale",
            prompt: "",
            min: 1,
            max: 10,
            min_label: "",
            max_label: ""
        };
    } else if (questionType === "multiple_choice") {
        question = {
            id: newId("q"),
            type: "multiple_choice",
            prompt: "",
            choice_set_id: choiceSetId ?? ""
        };
    } else {
        question = {
            id: newId("q"),
            type: "text",
            prompt: ""
        };
    }
    assessment.questions.push(question);
    return { document: draft, question };
}

export function updateQuestion(
    document: AssessmentsDocument,
    assessmentId: string,
    questionId: string,
    fields: {
        prompt?: string;
        min?: number;
        max?: number;
        min_label?: string;
        max_label?: string;
        choice_set_id?: string;
    }
): AssessmentsDocument {
    const draft = cloneDocument(document);
    const assessment = findAssessment(draft, assessmentId);
    if (!assessment) {
        throw new Error(`Assessment not found: ${assessmentId}`);
    }
    const index = assessment.questions.findIndex(item => item.id === questionId);
    if (index < 0) {
        throw new Error(`Question not found: ${questionId}`);
    }
    const question = assessment.questions[index];
    if (fields.prompt !== undefined) {
        question.prompt = clampPrompt(fields.prompt);
    }
    if (question.type === "scale") {
        if (fields.min !== undefined) {
            question.min = fields.min;
        }
        if (fields.max !== undefined) {
            question.max = fields.max;
        }
        if (fields.min_label !== undefined) {
            question.min_label = clampPrompt(fields.min_label);
        }
        if (fields.max_label !== undefined) {
            question.max_label = clampPrompt(fields.max_label);
        }
    }
    if (question.type === "multiple_choice" && fields.choice_set_id !== undefined) {
        question.choice_set_id = fields.choice_set_id;
    }
    return draft;
}

export function deleteQuestion(
    document: AssessmentsDocument,
    assessmentId: string,
    questionId: string
): AssessmentsDocument {
    const draft = cloneDocument(document);
    const assessment = findAssessment(draft, assessmentId);
    if (!assessment) {
        throw new Error(`Assessment not found: ${assessmentId}`);
    }
    assessment.questions = assessment.questions.filter(item => item.id !== questionId);
    return draft;
}

export function addChoiceSet(document: AssessmentsDocument): {
    document: AssessmentsDocument;
    choiceSet: ChoiceSet;
} {
    const draft = cloneDocument(document);
    if (draft.choice_sets.length >= MAX_CHOICE_SETS) {
        throw new Error(`Maximum ${MAX_CHOICE_SETS} choice sets allowed`);
    }
    const choiceSet: ChoiceSet = {
        id: newId("cs"),
        title: "",
        choices: [
            { id: newId("ch"), label: "" },
            { id: newId("ch"), label: "" }
        ]
    };
    draft.choice_sets.push(choiceSet);
    return { document: draft, choiceSet };
}

export function updateChoiceSet(
    document: AssessmentsDocument,
    choiceSetId: string,
    fields: { title?: string }
): AssessmentsDocument {
    const draft = cloneDocument(document);
    const choiceSet = findChoiceSet(draft, choiceSetId);
    if (!choiceSet) {
        throw new Error(`Choice set not found: ${choiceSetId}`);
    }
    if (fields.title !== undefined) {
        choiceSet.title = clampTitle(fields.title.trim());
    }
    return draft;
}

export function deleteChoiceSet(document: AssessmentsDocument, choiceSetId: string): AssessmentsDocument {
    if (isChoiceSetReferenced(document, choiceSetId)) {
        throw new Error("Cannot delete choice set that is referenced by a question");
    }
    const draft = cloneDocument(document);
    draft.choice_sets = draft.choice_sets.filter(item => item.id !== choiceSetId);
    return draft;
}

export function addChoice(
    document: AssessmentsDocument,
    choiceSetId: string
): { document: AssessmentsDocument; choice: Choice } {
    const draft = cloneDocument(document);
    const choiceSet = findChoiceSet(draft, choiceSetId);
    if (!choiceSet) {
        throw new Error(`Choice set not found: ${choiceSetId}`);
    }
    if (choiceSet.choices.length >= MAX_CHOICES_PER_SET) {
        throw new Error(`Maximum ${MAX_CHOICES_PER_SET} choices per set`);
    }
    const choice: Choice = { id: newId("ch"), label: "" };
    choiceSet.choices.push(choice);
    return { document: draft, choice };
}

export function updateChoice(
    document: AssessmentsDocument,
    choiceSetId: string,
    choiceId: string,
    label: string
): AssessmentsDocument {
    const draft = cloneDocument(document);
    const choiceSet = findChoiceSet(draft, choiceSetId);
    if (!choiceSet) {
        throw new Error(`Choice set not found: ${choiceSetId}`);
    }
    const choice = choiceSet.choices.find(item => item.id === choiceId);
    if (!choice) {
        throw new Error(`Choice not found: ${choiceId}`);
    }
    choice.label = clampPrompt(label);
    return draft;
}

export function deleteChoice(
    document: AssessmentsDocument,
    choiceSetId: string,
    choiceId: string
): AssessmentsDocument {
    const draft = cloneDocument(document);
    const choiceSet = findChoiceSet(draft, choiceSetId);
    if (!choiceSet) {
        throw new Error(`Choice set not found: ${choiceSetId}`);
    }
    if (choiceSet.choices.length <= 2) {
        throw new Error("Choice set must have at least 2 choices");
    }
    choiceSet.choices = choiceSet.choices.filter(item => item.id !== choiceId);
    return draft;
}

export function resolveChoiceSet(
    document: AssessmentsDocument,
    choiceSetId: string
): ChoiceSet | undefined {
    return findChoiceSet(document, choiceSetId);
}
